// A MIDI clip's editor in the room: the piano roll, docked under the view
// that opened it on a wide screen and a sheet over it on a phone — the same
// two homes the audio clip editor (clip-editor/ClipEditor.tsx) has, so either
// kind of clip opens where the other would. Docked and in the page's sheet
// it wears the audio editor's frame too (clip-editor/chrome.tsx): the clip's
// strip, the same tabs, the same Clip tab.

import { useEffect, type ReactNode } from "react";
import { soundingLabel } from "@web-shared/lib/sounding-label";
import type { WireNote } from "@shared/midi/notes.js";
import type { GhostNote } from "@web-shared/lib/ghost-notes";
import { clipColor, type LaneClip } from "@web-shared/lib/arrangement";
import { ClipPanel, EditorHeader, lengthLabel, type EditorHistory } from "@web-shared/components/clip-editor/chrome";
import { PianoRoll } from "./PianoRoll";

interface Props {
  clip: LaneClip;
  onChange?: (notes: WireNote[]) => void;
  onAudition?: (note: WireNote) => void | (() => void);
  onScaleLoop?: (factor: 2 | 0.5) => void;
  pending?: boolean;
  playheadBeats?: number;
  beatsPerBar?: number;
  roomScale?: { root: number; name: string; mode: boolean; intervals: readonly number[] };
  kit?: boolean;
  /** The other MIDI lanes' notes under this clip (lib/ghost-notes.ts) — drawn
   * dim behind it, FL Studio's ghost channels. Absent ⇒ no toggle. */
  ghostNotes?: readonly GhostNote[];
  /** A phone's sheet over the page; shorthand for `presentation: "modal"`. */
  modal?: boolean;
  /** Docked under the view, in the page's own bottom sheet, or a modal over
   * the page — the audio editor's three homes (clip-editor/ClipEditor.tsx). */
  presentation?: "docked" | "sheet" | "modal";
  onClose: () => void;
  /** Docked: fold down to the page's one-line strip (PianoRoll). */
  onMinimize?: () => void;
  /** The clip's colour as the timeline draws it, `#rrggbb`. */
  color?: string;
  /** The room's undo (the strip's ↶ ↷). */
  history?: EditorHistory;
  /** Hear the clip, looping; absent ⇒ no Play. */
  onPreview?: () => void;
  previewing?: boolean;
  /** Docked: the clip's actions (copy, split, delete…), in the strip. */
  actions?: ReactNode;
  /** The Clip tab's writes: name, colour, mute. Absent ⇒ no Clip tab. */
  onClipFields?: (fields: { name?: string; color?: number; muted?: boolean }) => void;
}

export function MidiClipEditor({
  modal: modalProp = false,
  presentation = modalProp ? "modal" : "docked",
  onClose,
  onMinimize,
  color: colorProp,
  history,
  onPreview,
  previewing,
  actions,
  onClipFields,
  ...roll
}: Props) {
  const modal = presentation === "modal";
  const column = presentation !== "docked";
  // A sheet holds the page still under it, as the audio editor's does.
  useEffect(() => {
    if (!modal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modal]);

  const { clip } = roll;
  const color = colorProp ?? clipColor(clip.color);
  const framed = presentation !== "modal";
  const piano = (
    <PianoRoll
      key={roll.clip.id}
      {...roll}
      onMinimize={framed ? undefined : onMinimize}
      editor={
        framed
          ? {
              presentation: presentation === "sheet" ? "sheet" : "docked",
              header:
                presentation === "docked" ? (
                  <EditorHeader
                    color={color}
                    name={clip.name}
                    muted={clip.muted}
                    meta={`${lengthLabel(clip.duration, roll.beatsPerBar)} · ${soundingLabel({ kind: "midi", audioKey: clip.audioKey ?? null, notes: clip.notes, noteCount: clip.noteCount })}`}
                    actions={actions}
                    onPreview={onPreview}
                    previewing={previewing}
                    history={history}
                    onMinimize={onMinimize}
                    onClose={onClose}
                  />
                ) : undefined,
              clipPanel: onClipFields ? (
                <ClipPanel
                  key={clip.id}
                  name={clip.name}
                  color={clip.color}
                  muted={clip.muted}
                  onName={(name) => onClipFields({ name })}
                  onColor={(c) => onClipFields({ color: c })}
                  onMuted={(muted) => onClipFields({ muted })}
                />
              ) : undefined,
            }
          : undefined
      }
      looping={roll.clip.slot !== undefined}
      // A finger has no double-click: a sheet opens drawing.
      startDrawing={column}
      // In the page's sheet the grid takes what the toolbar and lanes leave,
      // at whatever height the sheet is (it scrolls if a wrapped toolbar
      // leaves too little).
      fillBox={presentation === "sheet"}
      rollHeight={modal ? Math.max(220, Math.round(window.innerHeight * 0.88) - 300) : 260}
      className="rounded-none border-0"
      onClose={onClose}
    />
  );

  if (!modal) {
    return (
      <section
        role="dialog"
        aria-label={`Edit MIDI clip: ${roll.clip.name || "unnamed"}`}
        data-transport-keys
        className={presentation === "sheet" ? "flex min-h-0 flex-1 flex-col bg-sunk" : "flex h-[42dvh] min-h-[280px] shrink-0 flex-col border-t border-panelline bg-sunk"}
      >
        {piano}
      </section>
    );
  }

  return (
    <>
      <div className="fade-in fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <section
        role="dialog"
        aria-modal
        aria-label={`Edit MIDI clip: ${roll.clip.name || "unnamed"}`}
        data-transport-keys
        className="sheet-up fixed inset-x-0 bottom-0 z-50 flex h-[88dvh] flex-col overflow-hidden rounded-t-3xl bg-sunk pb-[env(safe-area-inset-bottom)] shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex shrink-0 items-center justify-between px-3 pt-2">
          <span className="mx-auto h-1.5 w-12 rounded-full bg-white/30" aria-hidden />
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{piano}</div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 border-t border-panelline py-3 text-sm font-medium text-foreground/80"
        >
          Done
        </button>
      </section>
    </>
  );
}
